import { Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteKeywords, adminGetKeywords } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminKeyword, IAdminKeywordList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";
import AddTagModal from "./AddTagModal";
import CreateKeywordModal from "./CreateKeywordModal";
import KeywordModal from "./KeywordModal";



export default function KeywordsByKeyword() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        searchKeyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

    const onSubmit = ({ searchKeyword }: ISearchForm) => {
        setPage(1);
        setSearchKeyword(searchKeyword);
    }
    
    /* query */

    const { data: keywordListData, isLoading: isKeywordListDataLoading } = useQuery<IAdminKeywordList>({
        queryKey: ['adminGetKeywords', page, size, searchKeyword],
        queryFn: () => adminGetKeywords({ page, size, searchKeyword }),
    });

    const totalPages = useMemo(() => keywordListData?.totalPages ?? 0, [keywordListData]);
    const currentPage = useMemo(() => keywordListData?.currentPage ?? 0, [keywordListData]);

    /* selection */

    const [selectedKeywordIds, setSelectedKeywordIds] = useState<string[]>([]);
    const currentPageKeywordIds = useMemo(() => keywordListData?.keywords.map((keyword) => keyword.id) ?? [], [keywordListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageKeywordIds.length > 0 && currentPageKeywordIds.every((id) => selectedKeywordIds.includes(id));
    }, [currentPageKeywordIds, selectedKeywordIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageKeywordIds.some((id) => selectedKeywordIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageKeywordIds, selectedKeywordIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedKeywordIds((prev) => prev.filter((id) => !currentPageKeywordIds.includes(id)));
        } else {
            setSelectedKeywordIds((prev) => Array.from(new Set([...prev, ...currentPageKeywordIds])));
        }
    };

    const toggleSelectOne = (keywordId: string) => {
        setSelectedKeywordIds((prev) => (prev.includes(keywordId) ? prev.filter((id) => id !== keywordId) : [...prev, keywordId]));
    };

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* columns */

    const columns = [
        { key: "keyword", label: "키워드 이름" },
        { key: "medicineCount", label: "약품 수" },
        { key: "tags", label: "연결된 태그" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" },
    ]

    /* create keyword modal */

    const { isOpen: isOpenCreateKeywordModal, onOpen: onOpenCreateKeywordModal, onClose: onCloseCreateKeywordModal } = useDisclosure();

    /* keyword modal */

    const { isOpen: isOpenKeywordModal, onOpen: onOpenKeywordModal, onClose: onCloseKeywordModal } = useDisclosure();
    const [activeKeywordId, setActiveKeywordId] = useState<string | null>(null);

    /* add tag modal */

    const { isOpen: isOpenAddTagModal, onOpen: onOpenAddTagModal, onClose: onCloseAddTagModal } = useDisclosure();
    const [activeTagKeywordId, setActiveTagKeywordId] = useState<string | null>(null);
    const [activeTagKeyword, setActiveTagKeyword] = useState<string | null>(null);
    
    /* tag Stack */

    const TagStack = (keyword: IAdminKeyword) => {
        return (
            <HStack 
                width={"100%"} 
                alignItems={"center"} 
                spacing={2}
            >
                <Box
                    as="button"
                    flexShrink={0}
                    onClick={() => {
                        setActiveTagKeywordId(keyword.id);
                        setActiveTagKeyword(keyword.keyword);
                        onOpenAddTagModal();
                    }}
                >
                    <MyIcon src={"/svg/plus-circle.svg"} alt="plus" width={24} height={24} />
                </Box>
                <HStack 
                    flexWrap={"wrap"} 
                    gap={1} 
                    flex={1} 
                    minWidth={0}
                    alignItems={"center"}
                >
                    {keyword.tags.map((tag) => (
                        <Text key={tag.id} whiteSpace={"nowrap"}>#{tag.tag}</Text>
                    ))}
                </HStack>
            </HStack>
        )
    }

    /* delete keywords modal */

    const { isOpen: isOpenDeleteKeywordsModal, onOpen: onOpenDeleteKeywordsModal, onClose: onCloseDeleteKeywordsModal } = useDisclosure();

    const onDeleteKeywords = () => {
        deleteKeywordsMutation.mutate({ keywordIds: selectedKeywordIds });
        onCloseDeleteKeywordsModal();
    }

    /* delete keywords mutation */

    const queryClient = useQueryClient();

    const toast = useToast();

    const deleteKeywordsMutation = useMutation({
        mutationFn: adminDeleteKeywords,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords', page, size, searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
            setSelectedKeywordIds([]);
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */


    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>키워드로 검색</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{keywordListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                        <Box as="button" onClick={onOpenCreateKeywordModal}>
                            <MyIcon src={"/svg/plus-circle.svg"} alt="plus" width={36} height={36} />
                        </Box>
                        <CreateKeywordModal
                            isOpen={isOpenCreateKeywordModal}
                            onClose={onCloseCreateKeywordModal}
                            page={page}
                            size={size}
                            searchKeyword={searchKeyword}
                        />
                    </HStack>
                </HStack>

                {/* search form */}

                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("searchKeyword", {required:false})}
                        type="text" 
                        placeholder="키워드로 검색하세요" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* trash button */}

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button 
                        size={"sm"} 
                        variant={"solid"} 
                        isDisabled={selectedKeywordIds.length === 0} 
                        opacity={selectedKeywordIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteKeywordsModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isOpenDeleteKeywordsModal} onClose={onCloseDeleteKeywordsModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedKeywordIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteKeywords}
                                    isLoading={deleteKeywordsMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {/* table */}

                {!isKeywordListDataLoading ? 
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox 
                                            isChecked={isAllCurrentPageSelected} 
                                            isIndeterminate={isSomeCurrentPageSelected} 
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {columns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {keywordListData?.keywords.map((keyword) => (
                                    <Tr key={keyword.id}>
                                        <Td>
                                            <Checkbox 
                                                isChecked={selectedKeywordIds.includes(keyword.id)}
                                                onChange={() => toggleSelectOne(keyword.id)}
                                            />
                                        </Td>
                                        {columns.map((column, index) => (
                                            <Td key={index}>
                                                {column.key === "keyword" ? 
                                                    <Button 
                                                        size={"md"} 
                                                        variant={"outline"}
                                                        borderColor={colors.blue}
                                                        onClick={() => { 
                                                            setActiveKeywordId(keyword.id);
                                                            onOpenKeywordModal();
                                                        }}
                                                    >
                                                        {String(keyword[column.key as keyof IAdminKeyword] ?? "-")}
                                                    </Button>
                                                 : column.key === "tags" ? TagStack(keyword)
                                                 : (column.key === "createdAt" || column.key === "updatedAt")
                                                    ? formatDateTimeKor(keyword[column.key as keyof IAdminKeyword] as string)
                                                    : String(keyword[column.key as keyof IAdminKeyword] ?? "-")}
                                            </Td>   
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                        : 
                    <Spinner color={colors.blue} />
                }

                {/* single keyword modal */}

                {activeKeywordId && (
                    <KeywordModal 
                        isOpen={isOpenKeywordModal}
                        onClose={() => { 
                            onCloseKeywordModal();
                            setActiveKeywordId(null);
                        }}
                        keywordId={activeKeywordId}
                        page={page}
                        size={size}
                        searchKeyword={searchKeyword}
                    />
                )}

                {/* add tag modal */}

                {activeTagKeywordId && activeTagKeyword && (
                    <AddTagModal
                        isOpen={isOpenAddTagModal}
                        onClose={() => {
                            onCloseAddTagModal();
                            setActiveTagKeywordId(null);
                            setActiveTagKeyword(null);
                        }}
                        keywordId={activeTagKeywordId}
                        keyword={activeTagKeyword}
                        page={page}
                        size={size}
                        searchKeyword={searchKeyword}
                    />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}